import { motion } from "framer-motion";
import { Link } from "react-router-dom";

type Props = {
  primaryImg?: string;   // main image
  secondaryImg?: string; // retro desk polaroid
};

export default function NomeraMainHeroRevamp({
  primaryImg = "/materials/smiling-nurse.jpg",
  secondaryImg = "/materials/retro-desk.png",
}: Props) {
  return (
    <section
      aria-label="Nomera hero"
      className="relative isolate overflow-hidden bg-gradient-to-b from-emerald-50/50 via-white to-white"
    >
      {/* subtle background wash */}
      <div
        aria-hidden
        className="pointer-events-none absolute -left-24 -top-28 h-[420px] w-[420px] rounded-full bg-emerald-200/25 blur-3xl"
      />

      <div className="mx-auto grid max-w-6xl grid-cols-1 items-center gap-10 px-6 py-16 sm:gap-14 sm:py-20 lg:grid-cols-2 lg:py-28">
        {/* LEFT: copy */}
        <div className="order-2 space-y-6 lg:order-1">
          <span className="inline-flex items-center gap-2 rounded-full bg-emerald-50 px-3 py-1 text-sm font-medium text-emerald-700">
            <span className="h-2 w-2 rounded-full bg-emerald-500" />
            Job matching, made simple
          </span>

          <h1 className="text-4xl font-semibold leading-[1.05] tracking-tight text-slate-900 sm:text-5xl lg:text-[56px]">
            Streamlined job matching for healthcare professionals and employers.
          </h1>

          <p className="max-w-xl text-lg/7 text-slate-600">
            We match qualified healthcare professionals with vetted facilities. Fast, direct, and no fluff.
          </p>

          {/* CTAs */}
          <div className="flex flex-wrap items-center gap-3 pt-2">
            <Link
              to="/healthcare-professionals"
              className="inline-flex items-center gap-2 rounded-2xl bg-emerald-600 px-5 py-3 text-base font-medium text-white shadow-sm transition hover:bg-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-400"
              aria-label="For Healthcare Professionals"
            >
              For Healthcare Professionals
              <svg viewBox="0 0 24 24" className="h-5 w-5" aria-hidden>
                <path fill="currentColor" d="M5 12h12M13 6l6 6-6 6" />
              </svg>
            </Link>

            <Link
              to="/employers"
              className="inline-flex items-center gap-2 rounded-2xl border border-slate-300 bg-white px-5 py-3 text-base font-medium text-slate-800 shadow-sm transition hover:border-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-300"
              aria-label="For Employers"
            >
              For Employers
            </Link>
          </div>

          {/* value bullets (balanced, accessible) */}
          <ul className="mt-6 grid w-full max-w-lg grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3 text-[15px] leading-relaxed text-slate-700">
            <li className="flex items-start gap-2">
              <span className="mt-2 inline-block h-2 w-2 rounded-full bg-emerald-500" aria-hidden />
              <span>Direct matches (no spam)</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="mt-2 inline-block h-2 w-2 rounded-full bg-indigo-500" aria-hidden />
              <span>Vetted healthcare professionals and facilities</span>
            </li>
            <li className="sm:col-span-2 flex items-start gap-2">
              <span className="mt-2 inline-block h-2 w-2 rounded-full bg-sky-500" aria-hidden />
              <span>Fast responses and clear status</span>
            </li>
          </ul>
        </div>

        {/* RIGHT: imagery */}
        <div className="order-1 lg:order-2">
          <div className="relative mx-auto max-w-xl">
            {/* main card */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-20%" }}
              transition={{ duration: 0.6, ease: "easeOut" }}
              className="relative rounded-3xl border border-slate-200 bg-white p-2 shadow-[0_10px_30px_rgba(2,6,23,0.06)]"
            >
              <img
                src={primaryImg}
                alt="Healthcare professional smiling at work"
                className="aspect-[4/3] w-full rounded-2xl object-cover"
                loading="eager"
                decoding="async"
              />
              <div className="pointer-events-none absolute inset-0 rounded-3xl [background:radial-gradient(120px_80px_at_85%_20%,rgba(16,185,129,0.08),transparent)]" />
            </motion.div>

            {/* polaroid – retro desk (bottom-right; crop to show top half) */}
            <motion.figure
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.15, duration: 0.55 }}
              className="absolute -bottom-10 -right-10 hidden w-[44%] rotate-[3deg] rounded-[18px] bg-white/95 p-3 shadow-xl ring-1 ring-slate-900/5 sm:block"
              aria-label="Retro workstation"
            >
              <img
                src={secondaryImg}
                alt="Healthcare professional at a retro workstation"
                className="aspect-[4/3] w-full rounded-[12px] object-cover"
                loading="lazy"
                decoding="async"
                style={{ objectPosition: '50% 30%' }}
              />
            </motion.figure>
          </div>
        </div>
      </div>

      {/* fade at the bottom */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 bottom-0 h-24 bg-gradient-to-b from-transparent to-white"
      />

      <style>{`
        @media (prefers-reduced-motion: reduce) {
          * {
            animation: none !important;
            transition: none !important;
          }
        }
      `}</style>
    </section>
  );
}


